import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "JefesInmediatos";


// ======================================================
// NORMALIZAR TEXTO
// ======================================================

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();


// ======================================================
// COMPONENTE
// ======================================================

const ListaJefes = () => {

  const [jefes, setJefes] = useState([]);

  const [loading, setLoading] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();


  // ====================================================
  // CONFIGURACIÓN AUTENTICACIÓN
  // ====================================================

  const authConfig = () => {

    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token") ||
      "";

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

  };


  // ====================================================
  // OBTENER PERMISOS DEL MÓDULO
  // ====================================================

  const getModuloJefes = () => {

    try {

      const permisosStorage =
        localStorage.getItem("permisos");

      const usuarioStorage =
        localStorage.getItem("usuario");

      const userStorage =
        localStorage.getItem("user");

      const usuariosStorage =
        localStorage.getItem("usuarios");


      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};


      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];


      const moduloBuscado =
        normalizarTexto(MODULO);


      return (
        modulos.find((modulo) => {

          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return (
            normalizarTexto(nombre) ===
            moduloBuscado
          );

        }) || {}
      );


    } catch (error) {

      console.error(
        "Error leyendo permisos:",
        error
      );

      return {};

    }

  };


  // ====================================================
  // PERMISOS
  // ====================================================

  const moduloJefes = useMemo(
    () => getModuloJefes(),
    []
  );


  const puedeLeer =
    moduloJefes?.puedeLeer === true ||
    moduloJefes?.leer === true ||
    moduloJefes?.ver === true ||
    moduloJefes?.listar === true ||
    moduloJefes?.read === true ||
    moduloJefes?.view === true;


  const puedeCrear =
    moduloJefes?.puedeCrear === true ||
    moduloJefes?.crear === true ||
    moduloJefes?.create === true;


  const puedeActualizar =
    moduloJefes?.puedeActualizar === true ||
    moduloJefes?.actualizar === true ||
    moduloJefes?.editar === true ||
    moduloJefes?.update === true;


  const puedeEliminar =
    moduloJefes?.puedeEliminar === true ||
    moduloJefes?.eliminar === true ||
    moduloJefes?.delete === true;


  // Permite entrar al módulo aunque solamente
  // tenga permiso de creación.
  const tieneAccesoModulo =
    puedeLeer ||
    puedeCrear ||
    puedeActualizar ||
    puedeEliminar;


  // ====================================================
  // MANEJAR SESIÓN
  // ====================================================

  const manejarErrorSesion = useCallback(
    (error) => {

      if (error?.response?.status === 401) {

        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {

          localStorage.removeItem("token");

          sessionStorage.removeItem("token");

          navigate("/", {
            replace: true,
          });

        });

        return true;

      }

      return false;

    },
    [navigate]
  );


  // ====================================================
  // ELIMINAR JEFE
  // ====================================================

  const deleteJefe = useCallback(
    async (id) => {

      if (!puedeEliminar) {

        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar jefes inmediatos.",
          "warning"
        );

        return;

      }


      try {

        // ==================================================
        // CONFIRMACIÓN
        // ==================================================

        const result = await Swal.fire({

          title: "¿Está seguro?",

          text:
            "Esta acción eliminará el jefe y su firma registrada. No podrá revertir este proceso.",

          icon: "warning",

          showCancelButton: true,

          confirmButtonColor: "#3085d6",

          cancelButtonColor: "#d33",

          confirmButtonText:
            "Sí, eliminar",

          cancelButtonText:
            "Cancelar",

        });


        if (!result.isConfirmed) {
          return;
        }


        setDeletingId(id);


        // ==================================================
        // ELIMINAR
        // ==================================================

        const { data } =
          await axios.delete(
            `/api/jefe/${id}`,
            authConfig()
          );


        // ==================================================
        // QUITAR DE LA TABLA
        // ==================================================

        setJefes((prev) =>
          prev.filter(
            (jefe) =>
              jefe._id !== id
          )
        );


        // ==================================================
        // MENSAJE
        // ==================================================

        Swal.fire({

          icon: "success",

          title:
            data?.message ||
            "Jefe eliminado correctamente",

          showConfirmButton: false,

          timer: 1500,

        });


      } catch (error) {

        console.error(
          "Error al eliminar jefe:",
          error
        );


        if (
          manejarErrorSesion(error)
        ) {
          return;
        }


        Swal.fire(

          "Error",

          error.response?.data
            ?.message ||
            "No se pudo eliminar el jefe.",

          "error"

        );


      } finally {

        setDeletingId(null);

      }

    },

    [
      puedeEliminar,
      manejarErrorSesion,
    ]
  );


  // ====================================================
  // OBTENER JEFES
  // ====================================================

  const getData = useCallback(
    async () => {

      if (!puedeLeer) {

        setJefes([]);

        return;

      }


      try {

        setLoading(true);


        // ==================================================
        // VALIDAR TOKEN
        // ==================================================

        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          "";


        if (!token) {

          Swal.fire(
            "Sesión no válida",
            "No se encontró el token de autenticación.",
            "warning"
          );

          setJefes([]);

          return;

        }


        // ==================================================
        // CONSULTAR BACKEND
        // ==================================================

        const { data } =
          await axios.get(
            "/api/jefe/listall/",
            authConfig()
          );


        // ==================================================
        // NORMALIZAR RESPUESTA
        // ==================================================

        const lista =
          Array.isArray(data?.jefes)
            ? data.jefes
            : Array.isArray(data)
            ? data
            : [];


        // Mostrar últimos primero
        setJefes(
          [...lista].reverse()
        );


      } catch (error) {

        console.error(
          "Error al cargar los jefes:",
          error
        );


        if (
          manejarErrorSesion(error)
        ) {
          return;
        }


        setJefes([]);


        Swal.fire(

          "Error",

          error.response?.data
            ?.message ||
            "No se pudieron cargar los jefes.",

          "error"

        );


      } finally {

        setLoading(false);

      }

    },

    [
      puedeLeer,
      manejarErrorSesion,
    ]
  );


  // ====================================================
  // CARGAR DATOS
  // ====================================================

  useEffect(() => {

    if (puedeLeer) {

      getData();

    }

  }, [
    puedeLeer,
    getData,
  ]);


  // ====================================================
  // DATOS PARA DATATABLE
  // ====================================================

  const dataTabla = useMemo(
    () =>

      jefes.map((jefe) => ({


        // Mantener datos originales
        ...jefe,


        // ==================================================
        // DOCUMENTO
        // ==================================================

        documentoJefe: (

          <div className="w-100">

            {jefe.documentoJefe ||
              "N/A"}

          </div>

        ),


        // ==================================================
        // NOMBRE
        // ==================================================

        nombre1Jefe: (

          <div className="w-100">

            <div
              title={[
                jefe.nombre1Jefe,
                jefe.nombre2Jefe,
              ]
                .filter(Boolean)
                .join(" ")}
            >

              {[
                jefe.nombre1Jefe,
                jefe.nombre2Jefe,
              ]
                .filter(Boolean)
                .join(" ") ||
                "N/A"}

            </div>

          </div>

        ),


        // ==================================================
        // APELLIDO
        // ==================================================

        apellido1Jefe: (

          <div className="w-100">

            <div
              title={[
                jefe.apellido1Jefe,
                jefe.apellido2Jefe,
              ]
                .filter(Boolean)
                .join(" ")}
            >

              {[
                jefe.apellido1Jefe,
                jefe.apellido2Jefe,
              ]
                .filter(Boolean)
                .join(" ") ||
                "N/A"}

            </div>

          </div>

        ),


        // ==================================================
        // TELÉFONO
        // ==================================================

        telefono1Jefe: (

          <div className="w-100">

            {jefe.telefono1Jefe ||
              "N/A"}

          </div>

        ),


        // ==================================================
        // EMAIL
        // ==================================================

        emailJefe: (

          <div className="w-100">

            <div

              className="text-truncate"

              style={{
                maxWidth: 280,
              }}

              title={
                jefe.emailJefe || ""
              }

            >

              {jefe.emailJefe ||
                "N/A"}

            </div>

          </div>

        ),


        // ==================================================
        // ESTADO FIRMA
        // ==================================================

        estadoFirma: (

          <div className="w-100">

            {jefe.firmaJefe ? (

              <span className="badge text-bg-success">

                <i className="fa-solid fa-circle-check me-1"></i>

                Registrada

              </span>

            ) : (

              <span className="badge text-bg-secondary">

                Sin firma

              </span>

            )}

          </div>

        ),


        // ==================================================
        // ACCIONES
        // ==================================================

        funciones: (

          <div className="d-flex gap-2 flex-wrap justify-content-end">


            {/* =============================================
                VER FIRMA
            ============================================= */}

            {puedeLeer && (

              <button

                className="
                  btn
                  btn-outline-secondary
                  btn-sm
                  d-inline-flex
                  align-items-center
                  justify-content-center
                "

                style={{
                  width: 34,
                  height: 34,
                }}

                data-bs-toggle="modal"

                data-bs-target={
                  `#previewFirmaJefe${jefe._id}`
                }

                title="Ver Firma"

                type="button"

                disabled={
                  loading ||
                  deletingId ===
                    jefe._id
                }

              >

                <i className="fa-solid fa-eye"></i>

              </button>

            )}


            {/* =============================================
                REGISTRAR / ACTUALIZAR FIRMA
            ============================================= */}

            {puedeActualizar && (

              <button

                className="
                  btn
                  btn-dark
                  btn-sm
                  d-inline-flex
                  align-items-center
                  justify-content-center
                "

                style={{
                  width: 34,
                  height: 34,
                }}

                onClick={() =>
                  navigate(
                    `/enteconformador/jefes/registrarfirma/${jefe._id}`,
                    {
                      state: {
                        jefe,
                      },
                    }
                  )
                }

                title={
                  jefe.firmaJefe
                    ? "Actualizar Firma"
                    : "Registrar Firma"
                }

                type="button"

                disabled={
                  loading ||
                  deletingId ===
                    jefe._id
                }

              >

                <i className="fa-solid fa-signature"></i>

              </button>

            )}


            {/* =============================================
                EDITAR
            ============================================= */}

            {puedeActualizar && (

              <button

                className="
                  btn
                  btn-warning
                  btn-sm
                  d-inline-flex
                  align-items-center
                  justify-content-center
                "

                style={{
                  width: 34,
                  height: 34,
                }}

                disabled={
                  loading ||
                  deletingId ===
                    jefe._id
                }

                onClick={() =>
                  navigate(
                    `/enteconformador/jefes/edit/${jefe._id}`,
                    {
                      state: {
                        jefe,
                      },
                    }
                  )
                }

                title="Editar Jefe"

                type="button"

              >

                <i className="fa-solid fa-pen-to-square"></i>

              </button>

            )}


            {/* =============================================
                ELIMINAR
            ============================================= */}

            {puedeEliminar && (

              <button

                className="
                  btn
                  btn-danger
                  btn-sm
                  d-inline-flex
                  align-items-center
                  justify-content-center
                "

                style={{
                  width: 34,
                  height: 34,
                }}

                disabled={
                  loading ||
                  deletingId ===
                    jefe._id
                }

                onClick={() =>
                  deleteJefe(
                    jefe._id
                  )
                }

                title="Eliminar Jefe"

                type="button"

              >

                {deletingId ===
                jefe._id ? (

                  <i className="fa-solid fa-spinner fa-spin text-white"></i>

                ) : (

                  <i className="fa-solid fa-trash"></i>

                )}

              </button>

            )}


          </div>

        ),


        // ==================================================
        // MODAL FIRMA
        // ==================================================

        modalFirma: (

          <div

            className="modal fade"

            id={
              `previewFirmaJefe${jefe._id}`
            }

            tabIndex="-1"

            aria-labelledby={
              `previewFirmaJefeLabel${jefe._id}`
            }

            aria-hidden="true"

            key={
              `modal-firma-jefe-${jefe._id}`
            }

          >


            <div className="modal-dialog modal-dialog-centered">


              <div className="modal-content border-0 rounded-4 shadow">


                {/* =========================================
                    HEADER MODAL
                ========================================= */}

                <div className="modal-header border-bottom">


                  <div>


                    <h6

                      className="modal-title fw-semibold mb-0"

                      id={
                        `previewFirmaJefeLabel${jefe._id}`
                      }

                    >

                      Firma del Jefe Inmediato

                    </h6>


                    <div className="small text-secondary">

                      {[
                        jefe.nombre1Jefe,
                        jefe.nombre2Jefe,
                        jefe.apellido1Jefe,
                        jefe.apellido2Jefe,
                      ]
                        .filter(Boolean)
                        .join(" ")}

                    </div>


                    <div className="small text-secondary">

                      Documento:{" "}

                      {jefe.documentoJefe ||
                        "N/A"}

                    </div>


                  </div>


                  <button

                    type="button"

                    className="btn-close"

                    data-bs-dismiss="modal"

                    aria-label="Cerrar"

                  ></button>


                </div>


                {/* =========================================
                    BODY MODAL
                ========================================= */}

                <div className="modal-body text-center">


                  {jefe.firmaJefe ? (

                    <>

                      <div className="p-3 bg-white">


                        <img

                          src={
                            jefe.firmaJefe
                          }

                          alt="Firma del Jefe Inmediato"

                          className="
                            img-fluid
                            border
                            p-2
                            rounded-3
                            bg-white
                          "

                          style={{
                            maxHeight: 260,
                            maxWidth: "100%",
                            objectFit:
                              "contain",
                          }}

                        />


                      </div>


                      <div className="mt-3">


                        <span className="badge text-bg-success">


                          <i className="fa-solid fa-circle-check me-1"></i>


                          Firma registrada


                        </span>


                      </div>

                    </>

                  ) : (

                    <div className="py-4">


                      <div className="display-6 mb-2">

                        ✍️

                      </div>


                      <p className="text-muted mb-3">

                        Este jefe inmediato no tiene firma registrada.

                      </p>


                      {puedeActualizar && (

                        <button

                          type="button"

                          className="btn btn-dark"

                          data-bs-dismiss="modal"

                          onClick={() =>
                            navigate(
                              `/enteconformador/jefes/registrarfirma/${jefe._id}`,
                              {
                                state: {
                                  jefe,
                                },
                              }
                            )
                          }

                        >

                          <i className="fa-solid fa-signature me-2"></i>

                          Registrar firma

                        </button>

                      )}


                    </div>

                  )}


                </div>


              </div>


            </div>


          </div>

        ),


      })),

    [
      jefes,
      loading,
      deletingId,
      puedeLeer,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteJefe,
    ]
  );


  // ====================================================
  // COLUMNAS
  // ====================================================

  const columns = useMemo(
    () => [

      {
        Header: "Documento",
        accessor: "documentoJefe",
      },

      {
        Header: "Nombre",
        accessor: "nombre1Jefe",
      },

      {
        Header: "Apellido",
        accessor: "apellido1Jefe",
      },

      {
        Header: "Teléfono",
        accessor: "telefono1Jefe",
      },

      {
        Header: "Email",
        accessor: "emailJefe",
        width: 280,
        hideHeaderOnMobile: true,
      },

      {
        Header: "Firma",
        accessor: "estadoFirma",
      },

      {
        Header: "Acciones",
        accessor: "funciones",
      },

    ],
    []
  );


  // ====================================================
  // SIN ACCESO AL MÓDULO
  // ====================================================

  if (!tieneAccesoModulo) {

    return (

      <div className="container py-5">


        <div className="alert alert-warning text-center rounded-4">

          No tiene permisos para acceder al módulo de Jefes Inmediatos.

        </div>


        <div className="text-center">

          <button

            className="btn btn-dark mt-3"

            type="button"

            onClick={() =>
              navigate("/inicio")
            }

          >

            Volver al inicio

          </button>

        </div>


      </div>

    );

  }


  // ====================================================
  // RENDER PRINCIPAL
  // ====================================================

  return (

    <div>


      <div className="container-fluid px-2 px-md-4">


        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">


          {/* =================================================
              HEADER
          ================================================= */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">


            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">


              <div>


                <h5 className="mt-2 mb-1 fw-semibold">

                  Jefes Inmediatos / Rectores

                </h5>


                <small className="text-secondary">

                  Gestión de jefes inmediatos, firmas y asociaciones institucionales.

                </small>


              </div>


              {/* =============================================
                  BOTONES HEADER
              ============================================= */}

              <div className="d-flex gap-2 flex-nowrap">


                {/* INICIO */}

                <button

                  className="btn btn-outline-dark flex-fill"

                  type="button"

                  onClick={() =>
                    navigate("/inicio")
                  }

                  title="Volver al inicio"

                  disabled={loading}

                >

                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Inicio

                </button>


                {/* ACTUALIZAR */}

                {puedeLeer && (

                  <button

                    className="btn btn-outline-secondary flex-fill"

                    type="button"

                    onClick={getData}

                    title="Actualizar"

                    disabled={loading}

                  >

                    <i
                      className={`fa-solid ${
                        loading
                          ? "fa-spinner fa-spin"
                          : "fa-rotate"
                      } me-2`}
                    ></i>

                    Actualizar

                  </button>

                )}


                {/* NUEVO */}

                {puedeCrear && (

                  <button

                    className="btn btn-dark flex-fill"

                    type="button"

                    disabled={loading}

                    onClick={() =>
                      navigate(
                        "/enteconformador/jefes/nuevo"
                      )
                    }

                    title="Crear Jefe"

                  >

                    <i className="fa-solid fa-plus me-2"></i>

                    Nuevo

                  </button>

                )}


              </div>


            </div>


          </div>


          {/* =================================================
              CONTENIDO
          ================================================= */}

          <div className="p-3 p-md-4">


            {/* =============================================
                SIN PERMISO DE LECTURA
            ============================================= */}

            {!puedeLeer ? (

              <div className="text-center text-secondary py-5">


                <div className="display-6 mb-2">

                  🔒

                </div>


                <div className="fw-semibold">

                  No tiene permiso para ver los jefes inmediatos registrados.

                </div>


                {puedeCrear && (

                  <>

                    <div className="small mb-3">

                      Pero sí puede registrar nuevos jefes inmediatos.

                    </div>


                    <button

                      className="btn btn-dark"

                      type="button"

                      onClick={() =>
                        navigate(
                          "/enteconformador/jefes/nuevo"
                        )
                      }

                    >

                      <i className="fa-solid fa-plus me-2"></i>

                      Nuevo jefe

                    </button>

                  </>

                )}


              </div>


            ) : loading ? (


              // =============================================
              // CARGANDO
              // =============================================

              <div className="text-center text-secondary py-5">


                <div

                  className="spinner-border"

                  role="status"

                  aria-hidden="true"

                ></div>


                <div className="mt-3">

                  Cargando jefes…

                </div>


              </div>


            ) : dataTabla.length > 0 ? (


              // =============================================
              // TABLA
              // =============================================

              <>


                <div className="overflow-hidden">

                  <DataTable
                    columns={columns}
                    data={dataTabla}
                  />

                </div>


                {/* ===========================================
                    MODALES DE FIRMA
                =========================================== */}

                {dataTabla.map(
                  (jefe) =>
                    jefe.modalFirma
                )}


              </>


            ) : (


              // =============================================
              // SIN REGISTROS
              // =============================================

              <div className="text-center text-secondary py-5">


                <div className="display-6 mb-2">

                  👤

                </div>


                <div className="fw-semibold">

                  No hay jefes registrados

                </div>


                <div className="small mb-3">

                  Crea el primer registro para iniciar la gestión.

                </div>


                {puedeCrear && (

                  <button

                    className="btn btn-dark"

                    type="button"

                    onClick={() =>
                      navigate(
                        "/enteconformador/jefes/nuevo"
                      )
                    }

                  >

                    <i className="fa-solid fa-plus me-2"></i>

                    Registrar jefe

                  </button>

                )}


              </div>


            )}


          </div>


        </div>


      </div>


    </div>

  );

};


export default ListaJefes;